import type {
  UpdateUserAttributesDataADto,
  UserAttributeADto,
} from '@cohort/admin-schemas/userAttributes';
import type {UserPropertyADto} from '@cohort/admin-schemas/userProperty';
import AppBadge from '@cohort/merchants/apps/AppBadge';
import Button from '@cohort/merchants/components/buttons/Button';
import HighlightText from '@cohort/merchants/components/HighlightText';
import Table from '@cohort/merchants/components/Table';
import {UserPropertyDataTypeBadge} from '@cohort/merchants/components/UserPropertyDataTypeBadge';
import {useCohortMutation} from '@cohort/merchants/hooks/api/Query';
import {userAttributesKeys, useUserAttributes} from '@cohort/merchants/hooks/api/UserAttributes';
import {useUserProperties} from '@cohort/merchants/hooks/api/UserProperties';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import {useCurrentUser} from '@cohort/merchants/hooks/contexts/currentUser';
import {useBasePageFilters} from '@cohort/merchants/hooks/filters/basePageFilters';
import {updateUserAttributes} from '@cohort/merchants/lib/api/UserAttributes';
import {getUserPropertiesSettingsRoute} from '@cohort/merchants/lib/Pages';
import EditableField from '@cohort/merchants/pages/users/user/overview/EditableField';
import type {PaginationDto} from '@cohort/shared/schema/common/pagination';
import {UserAttributeValueSchema} from '@cohort/shared/schema/common/userAttribute';
import {formatDate} from '@cohort/shared/utils/format';
import {zodResolver} from '@hookform/resolvers/zod';
import {useQueryClient} from '@tanstack/react-query';
import {Fragment} from 'react';
import {useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {z} from 'zod';

const PAGE_SIZE = 20;

export const UserAttributesFormSchema = z.object({
  data: z.array(
    z.object({
      id: z.string(),
      value: UserAttributeValueSchema.transform(value => (value === '' ? null : value)),
    })
  ),
});

type UserAttributesFormValues = z.infer<typeof UserAttributesFormSchema>;

type UserPropertiesTableProps = {
  userProperties: Array<UserPropertyADto>;
  userAttributes: Array<UserAttributeADto>;
  pagination?: PaginationDto & {onPageChange: (page: number) => void};
  isLoading?: boolean;
};
const UserPropertiesTable: React.FC<UserPropertiesTableProps> = ({
  userProperties,
  userAttributes,
  pagination,
  isLoading,
}) => {
  const {t} = useTranslation('pages', {keyPrefix: 'users.user.overview.userPropertiesSection'});
  const queryClient = useQueryClient();
  const user = useCurrentUser();
  const merchant = useCurrentMerchant();

  const {mutate: updateAttributes, isLoading: submitLoading} = useCohortMutation({
    mutationFn: async (data: UpdateUserAttributesDataADto) =>
      updateUserAttributes(merchant.id, data),
    notifyErrorMessage: t('notificationUpdateError'),
    notifySuccessMessage: t('notificationUpdateSuccess'),
    onSuccess: async () => {
      await queryClient.invalidateQueries(
        userAttributesKeys.list(merchant.id, {
          userId: user.id,
          userPropertyIds: userProperties.map(property => property.id),
        })
      );
      reset({});
    },
  });

  const {register, control, handleSubmit, reset, formState} = useForm<UserAttributesFormValues>({
    defaultValues: {
      data: userProperties.map(userProperty => {
        const userAttribute = userAttributes.find(
          attribute => attribute.userPropertyId === userProperty.id
        );
        return {
          id: userProperty.id,
          value: userAttribute ? userAttribute.value : '',
        };
      }),
    },
    resolver: zodResolver(UserAttributesFormSchema),
  });

  return (
    <form
      id="user-attributes"
      className="flex flex-col space-y-4"
      onSubmit={handleSubmit(data => {
        const values = data.data.map(userProperty => ({
          userPropertyId: userProperty.id,
          value: userProperty.value,
          userId: user.id,
        })) satisfies UpdateUserAttributesDataADto;
        return updateAttributes(values);
      })}
    >
      <Table
        emptyState={{
          isEmpty: userProperties.length === 0,
          title: t('titleEmpty'),
          description: t('descriptionEmpty'),
        }}
        headers={[
          {label: t('tableHeaderName')},
          {label: t('tableHeaderFormat')},
          {label: t('tableHeaderUserSync')},
          {label: t('tableHeaderValue'), width: '300px'},
          {label: t('tableHeaderCreatedAt')},
          {label: t('tableHeaderUpdatedAt')},
        ]}
        pagination={pagination}
        isLoading={isLoading}
      >
        {userProperties.map((userProperty, index) => {
          const userAttribute = userAttributes.find(
            attribute => attribute.userPropertyId === userProperty.id
          );
          return (
            <tr key={userProperty.id} className="last:pr-0! *:py-3 *:pr-3">
              <td className="font-medium">{userProperty.name}</td>
              <td>
                <UserPropertyDataTypeBadge userProperty={userProperty} />
              </td>
              <td>
                {userProperty.appId !== null ? <AppBadge appId={userProperty.appId} /> : <p>-</p>}
              </td>
              <td>
                <EditableField
                  control={control}
                  register={register}
                  name={`data.${index}.value`}
                  dataType={userProperty.dataType}
                  defaultValue={userAttribute?.value ?? ''}
                  withRemoveButton
                  disabled={userProperty.appId !== null}
                />
              </td>
              <td className="text-slate-500">
                {userAttribute && formatDate(userAttribute.createdAt)}
              </td>
              <td className="pr-0 text-slate-500">
                {userAttribute && formatDate(userAttribute.updatedAt)}
              </td>
            </tr>
          );
        })}
      </Table>
      <div className="flex justify-end">
        <Button disabled={!formState.isDirty} type="submit" loading={submitLoading}>
          {t('buttonSave')}
        </Button>
      </div>
    </form>
  );
};

const UserPropertiesSection: React.FC = () => {
  const user = useCurrentUser();
  const merchant = useCurrentMerchant();
  const navigate = useNavigate();
  const filters = useBasePageFilters(/^(\/test)?\/users\/.*\/overview$/u, null);

  const {data: properties} = useUserProperties(merchant.id, {
    page: filters.page,
    pageSize: PAGE_SIZE,
  });
  const [pagination, userProperties] = properties ?? [];

  const {data: userAttributes, isLoading: isAttributesLoading} = useUserAttributes(
    merchant.id,
    {
      userId: user.id,
      userPropertyIds: userProperties?.map(property => property.id) ?? [],
    },
    {
      enabled: userProperties !== undefined && userProperties.length > 0,
    }
  );

  if (userProperties === undefined) {
    return <Fragment />;
  }

  // should refresh when any attribute is added, removed or updated
  const refreshKey = userAttributes?.map(attribute => attribute.updatedAt.toString()).join() ?? '';

  return (
    <div className="flex flex-col space-y-4 pb-10">
      <UserPropertiesTable
        userProperties={userProperties}
        userAttributes={userAttributes ?? []}
        pagination={
          pagination !== undefined ? {...pagination, onPageChange: filters.setPage} : undefined
        }
        key={refreshKey}
        isLoading={isAttributesLoading}
      />
      <HighlightText
        type="info"
        text={
          <Trans
            i18nKey="users.user.overview.userPropertiesSection.helper"
            ns="pages"
            components={{
              pageLink: (
                <a
                  className="font-medium text-blue-600 underline"
                  href={getUserPropertiesSettingsRoute().path}
                  onClick={e => {
                    e.preventDefault();
                    navigate(getUserPropertiesSettingsRoute().path);
                  }}
                />
              ),
            }}
          />
        }
      />
    </div>
  );
};

export default UserPropertiesSection;
